import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, MessageSquare, ArrowRight, Shield, Clock, DollarSign, Users, FileText, AlertCircle, Award, ExternalLink } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { trackOfferAccepted, trackOfferDeclined, trackOfferCountered, trackDirectOfferPurchased } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function OfferPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [showCounter, setShowCounter] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [counterTerms, setCounterTerms] = useState('');
  const [responded, setResponded] = useState(null);
  const [acceptInfo, setAcceptInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/offers/${id}/view`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleRespond = async (action, extras = {}) => {
    setResponding(true);
    try {
      const { data: resp } = await axios.post(`${API}/offers/${id}/respond`, { action, ...extras });
      setResponded(action);
      setAcceptInfo(resp);
      // GA4 conversion events
      const evtParams = {
        offer_id: id,
        campaign_id: data?.campaign?.id,
        brand: data?.brand?.name,
        industry: data?.campaign?.industry,
      };
      if (action === 'accept') {
        trackOfferAccepted(evtParams);
        // Direct-offer "Purchase" conversion with $ value (flat_fee on the offer/campaign).
        const value = Number(
          data?.participant?.offered_amount ??
          data?.participant?.flat_fee ??
          data?.campaign?.flat_fee ??
          0
        );
        trackDirectOfferPurchased({
          offer_id: id,
          campaign_id: data?.campaign?.id,
          industry: data?.campaign?.industry,
          offering_type: data?.campaign?.offering_type,
          value,
          currency: 'USD',
        });
      }
      else if (action === 'decline') trackOfferDeclined(evtParams);
      else if (action === 'counter') trackOfferCountered(evtParams);
      if (action === 'accept' && resp.redirect) {
        setTimeout(() => navigate(resp.redirect), 3000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to respond'); }
    finally { setResponding(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center px-4">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-[#FF4343] mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold text-white mb-2">Offer Not Found</h2>
        <p className="text-[#9CA3AF] text-sm mb-6">This offer may have expired or been withdrawn.</p>
        <Link to="/login" className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-6 py-2 text-sm font-semibold">Go to Login</Link>
      </div>
    </div>
  );

  const { participant: part, campaign: camp, brand, executive: exec } = data;
  const flatFee = part.custom_flat_fee || camp.flat_fee;
  const cpmRate = part.custom_cpm_rate || camp.cpm_rate;
  const bonus = part.custom_bonus || camp.performance_bonus_amount;
  const isAccepted = part.status !== 'offered';
  const spotsLeft = (camp.spots_total || 0) - (camp.spots_filled || 0);

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="offer-page">
      <Toaster position="top-right" theme="dark" richColors />
      {/* Header */}
      <div className="border-b border-white/10 bg-[#111827]/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-7 w-7" />
            <span className="font-display text-lg font-bold"><span className="text-[#FF4343]">Freddy</span>Leads</span>
          </div>
          <Link to="/login" className="text-xs text-[#9CA3AF] hover:text-white transition-colors">Sign In</Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {/* Response Confirmation */}
        {responded && (
          <div className={`mb-8 p-6 text-center border ${responded === 'accept' ? 'bg-[#10B981]/10 border-[#10B981]/30' : responded === 'decline' ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`} data-testid="offer-response-confirmation">
            {responded === 'accept' && <>
              <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
              <h2 className="font-display text-2xl font-bold text-white mb-2">Offer Accepted!</h2>
              {acceptInfo?.needs_onboarding ? (
                <p className="text-sm text-[#9CA3AF] mb-4">Welcome to Freddy Leads! Create your account to get started...</p>
              ) : !acceptInfo?.has_stripe ? (
                <p className="text-sm text-[#9CA3AF] mb-4">Great! Let's set up your payout method so you can get paid...</p>
              ) : (
                <p className="text-sm text-[#9CA3AF] mb-4">Welcome aboard! Heading to your workspace...</p>
              )}
              <div className="w-6 h-6 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin mx-auto" />
            </>}
            {responded === 'decline' && <>
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <h2 className="font-display text-xl font-bold text-white mb-2">Offer Declined</h2>
              <p className="text-sm text-[#9CA3AF]">Thanks for your time. You can browse other campaigns anytime.</p>
              <Link to="/campaigns" className="inline-block mt-4 text-sm text-[#FF4343] hover:underline">Browse Campaigns →</Link>
            </>}
            {responded === 'counter' && <>
              <MessageSquare className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
              <h2 className="font-display text-xl font-bold text-white mb-2">Counter-Proposal Sent</h2>
              <p className="text-sm text-[#9CA3AF]">The brand has been notified. We'll follow up soon.</p>
              <Link to="/login" className="inline-block mt-4 text-sm text-[#FF4343] hover:underline">Go to Dashboard →</Link>
            </>}
          </div>
        )}

        {!responded && (
          <>
            {/* Offer Header */}
            <div className="text-center mb-8">
              <p className="text-xs text-[#FF4343] font-mono uppercase tracking-wider mb-2">Campaign Invitation</p>
              <h1 className="font-display text-2xl sm:text-4xl font-bold mb-2" data-testid="offer-title">{camp.title}</h1>
              <p className="text-[#9CA3AF] text-sm">
                On behalf of <strong className="text-white">{brand.name}</strong> {brand.verified && <span className="text-[#FF4343]">✓</span>}
              </p>
            </div>

            {/* Addressed To */}
            <div className="bg-[#111827] border border-white/10 p-5 mb-6">
              <p className="text-xs text-[#6B7280] mb-1">This offer is for</p>
              <p className="text-lg text-white font-semibold">{exec.name || part.executive_name}</p>
              {part.offer_message && (
                <p className="text-sm text-[#9CA3AF] mt-3 italic border-l-2 border-[#FF4343] pl-3">"{part.offer_message}"</p>
              )}
              {part.offer_type === 'bespoke' && (
                <span className="inline-block mt-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20">Bespoke Offer</span>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Campaign Brief */}
              <div className="lg:col-span-2 space-y-5">
                <div className="bg-[#111827] border border-white/10 p-5 sm:p-6">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#FF4343]" /> Campaign Brief</h3>
                  <p className="text-sm text-[#9CA3AF] leading-relaxed mb-5">{camp.description}</p>

                  <div className="flex flex-wrap gap-2 mb-5">
                    <span className="text-xs px-2 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{camp.industry}</span>
                    {camp.platforms?.map(p => <span key={p} className="text-xs px-2 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{p}</span>)}
                    {camp.posts_per_exec > 1 && <span className="text-xs px-2 py-1 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20">{camp.posts_per_exec} Posts</span>}
                  </div>

                  {camp.talking_points?.length > 0 && (
                    <div className="mb-5">
                      <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> Talking Points</h4>
                      <ul className="space-y-1.5">
                        {camp.talking_points.map((tp, i) => <li key={i} className="text-sm text-[#9CA3AF] flex items-start gap-2"><span className="text-[#FF4343] mt-0.5">•</span>{tp}</li>)}
                      </ul>
                    </div>
                  )}

                  {camp.guardrails?.length > 0 && (
                    <div className="mb-5">
                      <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-red-400" /> Brand Guidelines</h4>
                      <ul className="space-y-1.5">
                        {camp.guardrails.map((g, i) => <li key={i} className="text-sm text-[#9CA3AF] flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span>{g}</li>)}
                      </ul>
                    </div>
                  )}

                  {camp.example_post && (
                    <div className="border-t border-white/10 pt-4">
                      <h4 className="text-xs font-semibold text-white mb-2">Example Post</h4>
                      <div className="bg-white/5 p-4 text-sm text-[#9CA3AF] italic leading-relaxed">"{camp.example_post}"</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Compensation Sidebar */}
              <div className="space-y-5">
                <div className="bg-[#111827] border border-[#FF4343]/20 p-5">
                  <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-[#FF4343]" /> Your Compensation</h3>
                  <div className="space-y-3 mb-5">
                    {flatFee && (
                      <div className="flex justify-between"><span className="text-sm text-[#9CA3AF]">Flat Fee</span><span className="font-mono text-[#FF4343] font-bold text-lg">${Number(flatFee).toLocaleString()}</span></div>
                    )}
                    {cpmRate && (
                      <div className="flex justify-between"><span className="text-sm text-[#9CA3AF]">CPM Rate</span><span className="font-mono text-[#FF4343] font-bold text-lg">${cpmRate}</span></div>
                    )}
                    {camp.floor_guarantee && (
                      <div className="flex justify-between"><span className="text-sm text-[#9CA3AF]">Floor Guarantee</span><span className="font-mono text-white font-bold">${Number(camp.floor_guarantee).toLocaleString()}</span></div>
                    )}
                    {bonus && (
                      <div className="flex justify-between"><span className="text-sm text-[#9CA3AF]">Performance Bonus</span><span className="font-mono text-[#10B981] font-bold">+${Number(bonus).toLocaleString()}</span></div>
                    )}
                  </div>
                  <div className="border-t border-white/10 pt-4 space-y-2 text-xs text-[#6B7280]">
                    <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Deadline: {camp.deadline ? new Date(camp.deadline).toLocaleDateString() : 'Open'}</div>
                    <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {spotsLeft > 0 ? `${spotsLeft} spots remaining` : 'Limited spots'}</div>
                    {brand.website && <a href={brand.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#FF4343] hover:underline"><ExternalLink className="w-3.5 h-3.5" /> {brand.name} website</a>}
                  </div>
                </div>

                {/* Action Buttons */}
                {!isAccepted && (
                  <div className="space-y-2" data-testid="offer-actions">
                    <button onClick={() => handleRespond('accept')} disabled={responding} data-testid="accept-offer-btn"
                      className="w-full flex items-center justify-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3.5 text-sm font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
                      {responding ? 'Processing...' : 'Accept Offer'} <ArrowRight className="w-4 h-4" />
                    </button>
                    <button onClick={() => setShowCounter(true)} disabled={responding} data-testid="counter-offer-btn"
                      className="w-full flex items-center justify-center gap-2 border border-white/20 text-white py-3 text-sm font-medium hover:bg-white/5 transition-all">
                      <MessageSquare className="w-4 h-4" /> Counter-Propose
                    </button>
                    <button onClick={() => setShowDecline(true)} disabled={responding} data-testid="decline-offer-btn"
                      className="w-full text-center text-xs text-[#6B7280] hover:text-red-400 py-2 transition-colors">
                      Decline This Offer
                    </button>
                  </div>
                )}

                {isAccepted && (
                  <div className="bg-white/5 border border-white/10 p-4 text-center">
                    <p className="text-sm text-[#9CA3AF]">This offer has been {part.status}.</p>
                    <Link to="/login" className="text-sm text-[#FF4343] hover:underline mt-2 inline-block">Go to Dashboard →</Link>
                  </div>
                )}

                {/* Powered By */}
                <div className="text-center pt-2">
                  <p className="text-[10px] text-[#6B7280]">Powered by <span className="text-white font-medium">Freddy Leads</span> by Freddy Media</p>
                </div>
              </div>
            </div>

            {/* Decline Modal */}
            {showDecline && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowDecline(false)} />
                <div className="relative glass max-w-sm w-full p-6" data-testid="decline-modal">
                  <h3 className="font-display text-lg font-bold mb-3">Decline Offer</h3>
                  <p className="text-xs text-[#9CA3AF] mb-4">Optionally share a reason — this helps brands improve future offers.</p>
                  <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3} placeholder="Reason (optional)..." data-testid="decline-reason"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-4" />
                  <div className="flex gap-2">
                    <button onClick={() => handleRespond('decline', { reason: declineReason })} disabled={responding} data-testid="confirm-decline-btn"
                      className="flex-1 border border-red-500/50 text-red-400 py-2 text-xs font-medium hover:bg-red-500/10 transition-all disabled:opacity-50">
                      {responding ? 'Declining...' : 'Confirm Decline'}
                    </button>
                    <button onClick={() => setShowDecline(false)} className="border border-white/20 text-[#9CA3AF] px-4 py-2 text-xs hover:text-white transition-all">Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Counter Modal */}
            {showCounter && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCounter(false)} />
                <div className="relative glass max-w-md w-full p-6" data-testid="counter-modal">
                  <h3 className="font-display text-lg font-bold mb-3">Counter-Propose</h3>
                  <p className="text-xs text-[#9CA3AF] mb-4">Describe your preferred terms — compensation, timeline, or scope changes.</p>
                  <textarea value={counterTerms} onChange={e => setCounterTerms(e.target.value)} rows={4} placeholder="e.g., I'd like $2,000 flat fee instead of $1,500, or I'd prefer to post on X instead of LinkedIn..." data-testid="counter-terms"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-4" />
                  <div className="flex gap-2">
                    <button onClick={() => handleRespond('counter', { counter_terms: counterTerms })} disabled={responding || !counterTerms.trim()} data-testid="send-counter-btn"
                      className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                      {responding ? 'Sending...' : 'Send Counter-Proposal'}
                    </button>
                    <button onClick={() => setShowCounter(false)} className="border border-white/20 text-[#9CA3AF] px-4 py-2 text-xs hover:text-white transition-all">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
